import {
  Project,
  ProjectSummary,
  Template,
  EmailSent,
  PartnerInfos,
  Broadcast,
  LibraryDocument,
  SyncOperation,
} from "types";
import { EmailTemplate } from "types/email_template";
import { FormTemplate } from "types/generated_document";
import { Planning } from "types/planning";
import { PlanningTemplate } from "types/planning_template";
import { create } from "zustand";
import {
  projectsApi,
  partnersApi,
  broadcastsApi,
  emailsSentApi,
  libraryDocumentsApi,
  emailTemplatesApi,
  projectTemplatesApi,
  formTemplatesApi,
  syncOperationsApi,
  planningsApi,
  planningTemplatesApi,
} from "@services/api";

const toProjectSummary = (p: Project): ProjectSummary => ({
  id: p.id,
  status: p.status,
  org_id: p.org_id,
  infos: {
    general: {
      name: p.infos.general.name,
      num: p.infos.general.num,
      city: p.infos.general.city,
      creation_date: p.infos.general.creation_date,
      photo_file: p.infos.general.photo_file ? { url: p.infos.general.photo_file.url } : undefined,
      coordinates: p.infos.general.coordinates,
    },
    team: {
      commercial_id: p.infos.team.commercial_id,
      comptability_id: p.infos.team.comptability_id,
    },
  },
});

interface ProjectStore {
  activeProject: Project | undefined;
  setActiveProject: (folder: Project | undefined) => void;
  clearActiveProject: () => void;

  // Projects (API-connected)
  projects: ProjectSummary[];
  isLoadingProjects: boolean;
  hasMoreProjects: boolean;
  hasPrevProjects: boolean;
  projectsPage: number;
  projectsStatus: string;
  projectsSortBy: string;
  projectsSortDir: string;
  projectsError: string | null;
  _fetchedOrgId: string | undefined;
  fetchProjects: (orgId: string, status?: string) => Promise<void>;
  fetchNextPage: (orgId: string) => Promise<void>;
  fetchPrevPage: (orgId: string) => Promise<void>;
  setProjectsSort: (orgId: string, sortBy: string, sortDir: string) => Promise<void>;
  isFetchingProjectDetail: boolean;
  fetchProject: (id: string) => Promise<Project | undefined>;
  setProjects: (projects: ProjectSummary[]) => void;
  addProject: (project: Project) => void;
  updateProject: (project: Project) => void;
  removeProjects: (ids: string[]) => void;

  // Sent emails (API-connected)
  sentEmails: EmailSent[];
  isLoadingSentEmails: boolean;
  fetchSentEmails: (orgId: string, projectId?: string) => Promise<void>;
  setSentEmails: (emails: EmailSent[]) => void;
  addSentEmail: (email: EmailSent, orgId: string) => void;
  removeSentEmails: (ids: string[]) => void;

  // Partners (API-connected) — full list for dropdowns
  partners: PartnerInfos[];
  isLoadingPartners: boolean;
  fetchPartners: (orgId: string) => Promise<void>;
  setPartners: (partners: PartnerInfos[]) => void;
  addPartner: (partner: PartnerInfos, orgId: string) => void;
  updatePartner: (partner: PartnerInfos) => void;
  removePartners: (ids: string[]) => void;

  // Partners paginated list — for the Artisans screen
  partnersList: PartnerInfos[];
  isLoadingPartnersList: boolean;
  hasMorePartnersList: boolean;
  hasPrevPartnersList: boolean;
  partnersListPage: number;
  partnersListCompanyType: string;
  partnersListSortBy: string;
  partnersListSortDir: string;
  fetchPartnersList: (orgId: string, companyType?: string) => Promise<void>;
  fetchNextPagePartners: (orgId: string) => Promise<void>;
  fetchPrevPagePartners: (orgId: string) => Promise<void>;
  setPartnersSort: (orgId: string, sortBy: string, sortDir: string) => Promise<void>;

  // Broadcasts (API-connected)
  broadcasts: Broadcast[];
  isLoadingBroadcasts: boolean;
  fetchBroadcasts: (orgId: string) => Promise<void>;
  setBroadcasts: (broadcasts: Broadcast[]) => void;
  addBroadcast: (broadcast: Broadcast, orgId: string) => void;
  updateBroadcast: (broadcast: Broadcast) => void;
  removeBroadcasts: (ids: string[]) => void;

  // Library documents (API-connected)
  libraryDocuments: LibraryDocument[];
  isLoadingLibraryDocuments: boolean;
  fetchLibraryDocuments: (orgId: string) => Promise<void>;
  addLibraryDocument: (doc: LibraryDocument, orgId: string) => void;
  removeLibraryDocuments: (ids: string[]) => void;

  // Email templates (API-connected)
  emailTemplates: EmailTemplate[];
  isLoadingEmailTemplates: boolean;
  fetchEmailTemplates: (orgId: string) => Promise<void>;
  setEmailTemplates: (templates: EmailTemplate[]) => void;
  addEmailTemplate: (template: EmailTemplate, orgId: string) => void;
  updateEmailTemplate: (template: EmailTemplate) => void;
  removeEmailTemplates: (ids: string[]) => void;

  // Project templates (API-connected)
  projectTemplates: Template[];
  isLoadingProjectTemplates: boolean;
  fetchProjectTemplates: (orgId: string) => Promise<void>;
  setProjectTemplates: (templates: Template[]) => void;
  addProjectTemplate: (template: Template) => void;
  updateProjectTemplate: (template: Template) => void;
  removeProjectTemplates: (ids: string[]) => void;

  // Form templates (API-connected)
  formTemplates: FormTemplate[];
  isLoadingFormTemplates: boolean;
  fetchFormTemplates: (orgId: string) => Promise<void>;
  setFormTemplates: (templates: FormTemplate[]) => void;
  addFormTemplate: (template: FormTemplate) => void;
  updateFormTemplate: (template: FormTemplate) => void;
  removeFormTemplates: (ids: string[]) => void;

  // Sync operations (API-connected)
  syncOperations: SyncOperation[];
  isLoadingSyncOperations: boolean;
  fetchSyncOperations: (orgId: string) => Promise<void>;
  setSyncOperations: (operations: SyncOperation[]) => void;
  clearCompletedSyncOperations: (orgId: string) => Promise<void>;

  // Plannings (API-connected)
  plannings: Planning[];
  isLoadingPlannings: boolean;
  fetchPlannings: (orgId: string, projectId?: string) => Promise<void>;
  addPlanning: (planning: Planning) => void;
  updatePlanning: (planning: Planning) => void;
  removePlannings: (ids: string[]) => void;

  // Planning templates (API-connected)
  planningTemplates: PlanningTemplate[];
  isLoadingPlanningTemplates: boolean;
  fetchPlanningTemplates: (orgId: string) => Promise<void>;
  setPlanningTemplates: (templates: PlanningTemplate[]) => void;
  addPlanningTemplate: (template: PlanningTemplate) => void;
  updatePlanningTemplate: (template: PlanningTemplate) => void;
  removePlanningTemplates: (ids: string[]) => void;
}

export const useProjectStore = create<ProjectStore>((set) => ({
  activeProject: undefined,

  setActiveProject: (project) => {
    set({ activeProject: project });
  },

  clearActiveProject: () => {
    set({ activeProject: undefined });
  },

  // --- Projects (API-connected) ---
  projects: [],
  isLoadingProjects: false,
  hasMoreProjects: false,
  hasPrevProjects: false,
  projectsPage: 0,
  projectsStatus: "",
  projectsSortBy: "",
  projectsSortDir: "asc",
  projectsError: null,
  _fetchedOrgId: undefined,
  isFetchingProjectDetail: false,

  fetchProjects: async (orgId: string, status?: string) => {
    const PAGE_SIZE = 50;
    const state = useProjectStore.getState();
    const normalizedStatus = status === "all" ? undefined : status;
    if (state.isLoadingProjects || (state._fetchedOrgId === orgId && state.projectsStatus === (normalizedStatus ?? ""))) return;
    set({ isLoadingProjects: true, projectsError: null, projectsPage: 0, _fetchedOrgId: orgId, projectsStatus: normalizedStatus ?? "" });
    try {
      const { projectsSortBy, projectsSortDir } = useProjectStore.getState();
      const projects = await projectsApi.list({ org_id: orgId, status: normalizedStatus, limit: PAGE_SIZE, offset: 0, sort_by: projectsSortBy || undefined, sort_dir: projectsSortDir });
      set({
        projects: projects.map(toProjectSummary),
        isLoadingProjects: false,
        projectsPage: 1,
        hasMoreProjects: projects.length === PAGE_SIZE,
        hasPrevProjects: false,
      });
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      set({ projects: [], isLoadingProjects: false, _fetchedOrgId: undefined, projectsError: "Échec du chargement des projets" });
    }
  },

  fetchNextPage: async (orgId: string) => {
    const PAGE_SIZE = 50;
    const { isLoadingProjects, hasMoreProjects, projectsPage, projectsStatus, projectsSortBy, projectsSortDir } = useProjectStore.getState();
    if (isLoadingProjects || !hasMoreProjects) return;
    set({ isLoadingProjects: true });
    try {
      const next = await projectsApi.list({ org_id: orgId, status: projectsStatus || undefined, limit: PAGE_SIZE, offset: projectsPage * PAGE_SIZE, sort_by: projectsSortBy || undefined, sort_dir: projectsSortDir });
      set((state) => ({ projects: next.map(toProjectSummary), isLoadingProjects: false, projectsPage: state.projectsPage + 1, hasMoreProjects: next.length === PAGE_SIZE, hasPrevProjects: true }));
    } catch (error: any) {
      console.error("Error fetching next page:", error);
      set({ isLoadingProjects: false });
    }
  },

  fetchPrevPage: async (orgId: string) => {
    const PAGE_SIZE = 50;
    const { isLoadingProjects, hasPrevProjects, projectsPage, projectsStatus, projectsSortBy, projectsSortDir } = useProjectStore.getState();
    if (isLoadingProjects || !hasPrevProjects) return;
    const prevPage = projectsPage - 2;
    set({ isLoadingProjects: true });
    try {
      const prev = await projectsApi.list({ org_id: orgId, status: projectsStatus || undefined, limit: PAGE_SIZE, offset: prevPage * PAGE_SIZE, sort_by: projectsSortBy || undefined, sort_dir: projectsSortDir });
      set((state) => ({ projects: prev.map(toProjectSummary), isLoadingProjects: false, projectsPage: state.projectsPage - 1, hasMoreProjects: true, hasPrevProjects: prevPage > 0 }));
    } catch (error: any) {
      console.error("Error fetching prev page:", error);
      set({ isLoadingProjects: false });
    }
  },

  setProjectsSort: async (orgId: string, sortBy: string, sortDir: string) => {
    const FIELD_MAP: Record<string, string> = {
      name: "infos.general.name",
      city: "infos.general.city",
      creation_date: "infos.general.creation_date",
      status: "status",
    };
    const mappedField = FIELD_MAP[sortBy] || sortBy;
    const { projectsStatus } = useProjectStore.getState();
    set({ projectsSortBy: mappedField, projectsSortDir: sortDir, _fetchedOrgId: undefined });
    const PAGE_SIZE = 50;
    set({ isLoadingProjects: true, projectsPage: 0, hasPrevProjects: false });
    try {
      const projects = await projectsApi.list({ org_id: orgId, status: projectsStatus || undefined, limit: PAGE_SIZE, offset: 0, sort_by: sortBy || undefined, sort_dir: sortDir });
      set({ projects: projects.map(toProjectSummary), isLoadingProjects: false, projectsPage: 1, hasMoreProjects: projects.length === PAGE_SIZE, hasPrevProjects: false });
    } catch (error: any) {
      set({ isLoadingProjects: false });
    }
  },

  fetchProject: async (id: string) => {
    set({ isFetchingProjectDetail: true });
    try {
      const project = await projectsApi.get(id);
      const summary = toProjectSummary(project);
      set((state) => ({
        isFetchingProjectDetail: false,
        projects: state.projects.some((p) => p.id === id)
          ? state.projects.map((p) => (p.id === id ? summary : p))
          : [...state.projects, summary],
      }));
      return project;
    } catch (error: any) {
      console.error("Error fetching project:", error);
      set({ isFetchingProjectDetail: false });
      return undefined;
    }
  },

  setProjects: (projects: ProjectSummary[]) => {
    set({ projects });
  },

  addProject: (project: Project) => {
    // Clear _fetchedOrgId so the list re-fetches on next visit and includes this new project
    set((state) => ({ projects: [...state.projects, toProjectSummary(project)], _fetchedOrgId: undefined }));
  },

  updateProject: (project: Project) => {
    set((state) => ({
      projects: state.projects.map((p) =>
        p.id === project.id ? toProjectSummary(project) : p,
      ),
    }));
    projectsApi.save(project).catch((error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message;
      console.error(`❌ Error saving project ${project.id} (HTTP ${status}):`, detail, error);
    });
  },

  removeProjects: (ids: string[]) => {
    set((state) => ({
      projects: state.projects.filter((p) => !ids.includes(p.id)),
    }));
    ids.forEach((id) => {
      projectsApi.delete(id).catch((error) => {
        console.error("Error deleting project from API:", error);
      });
    });
  },

  // --- Sent emails (API-connected) ---
  sentEmails: [],
  isLoadingSentEmails: false,

  fetchSentEmails: async (orgId: string, projectId?: string) => {
    set({ isLoadingSentEmails: true });
    try {
      const fetched = await emailsSentApi.list({ org_id: orgId, project_id: projectId });
      if (projectId) {
        // Merge project emails into existing store without wiping other projects' emails
        set((state) => {
          const merged = [...state.sentEmails.filter((e) => e.project_id !== projectId), ...fetched];
          return { sentEmails: merged, isLoadingSentEmails: false };
        });
      } else {
        set({ sentEmails: fetched, isLoadingSentEmails: false });
      }
    } catch (error: any) {
      console.error("Error fetching sent emails:", error);
      set({ isLoadingSentEmails: false });
    }
  },

  setSentEmails: (emails: EmailSent[]) => {
    set({ sentEmails: emails });
  },

  addSentEmail: (email: EmailSent, orgId: string) => {
    set((state) => ({ sentEmails: [...state.sentEmails, email] }));
    emailsSentApi.create({ ...email, org_id: orgId } as any).catch((error) => {
      console.error("Error creating sent email in API:", error);
    });
  },

  removeSentEmails: (ids: string[]) => {
    set((state) => ({
      sentEmails: state.sentEmails.filter((e) => !ids.includes(e.id)),
    }));
    ids.forEach((id) => {
      emailsSentApi.delete(id).catch((error) => {
        console.error("Error deleting sent email from API:", error);
      });
    });
  },

  // --- Partners (API-connected) ---
  partners: [],
  isLoadingPartners: false,

  fetchPartners: async (orgId: string) => {
    set({ isLoadingPartners: true });
    try {
      const partners = await partnersApi.list({ org_id: orgId, limit: 1000 });
      set({ partners, isLoadingPartners: false });
    } catch (error: any) {
      console.error("Error fetching partners:", error);
      set({ partners: [], isLoadingPartners: false });
    }
  },

  setPartners: (partners: PartnerInfos[]) => {
    set({ partners });
  },

  addPartner: (partner: PartnerInfos, orgId: string) => {
    set((state) => ({
      partners: [...state.partners, partner],
      partnersList: [partner, ...state.partnersList],
    }));
    partnersApi.create({ ...partner, org_id: orgId }).catch((error) => {
      console.error("Error creating partner in API:", error);
    });
  },

  updatePartner: (partner: PartnerInfos) => {
    set((state) => ({
      partners: state.partners.map((p) => (p.id === partner.id ? partner : p)),
      partnersList: state.partnersList.map((p) => (p.id === partner.id ? partner : p)),
    }));
    partnersApi.update(partner.id, partner).catch((error) => {
      console.error("Error updating partner in API:", error);
    });
  },

  removePartners: (ids: string[]) => {
    set((state) => ({
      partners: state.partners.filter((p) => !ids.includes(p.id)),
      partnersList: state.partnersList.filter((p) => !ids.includes(p.id)),
    }));
    ids.forEach((id) => {
      partnersApi.delete(id).catch((error) => {
        console.error("Error deleting partner from API:", error);
      });
    });
  },

  // --- Partners paginated list ---
  partnersList: [],
  isLoadingPartnersList: false,
  hasMorePartnersList: false,
  hasPrevPartnersList: false,
  partnersListPage: 0,
  partnersListCompanyType: "",
  partnersListSortBy: "",
  partnersListSortDir: "asc",

  fetchPartnersList: async (orgId: string, companyType?: string) => {
    const PAGE_SIZE = 50;
    const type = companyType === "all" ? undefined : companyType;
    const { partnersListSortBy, partnersListSortDir } = useProjectStore.getState();
    set({ isLoadingPartnersList: true, partnersListCompanyType: type ?? "" });
    try {
      const partners = await partnersApi.list({ org_id: orgId, company_type: type, limit: PAGE_SIZE, offset: 0, sort_by: partnersListSortBy || undefined, sort_dir: partnersListSortDir });
      set({ partnersList: partners, isLoadingPartnersList: false, partnersListPage: 1, hasMorePartnersList: partners.length === PAGE_SIZE, hasPrevPartnersList: false });
    } catch (error: any) {
      console.error("Error fetching partners list:", error);
      set({ partnersList: [], isLoadingPartnersList: false });
    }
  },

  fetchNextPagePartners: async (orgId: string) => {
    const PAGE_SIZE = 50;
    const { isLoadingPartnersList, hasMorePartnersList, partnersListPage, partnersListCompanyType, partnersListSortBy, partnersListSortDir } = useProjectStore.getState();
    if (isLoadingPartnersList || !hasMorePartnersList) return;
    set({ isLoadingPartnersList: true });
    try {
      const next = await partnersApi.list({ org_id: orgId, company_type: partnersListCompanyType || undefined, limit: PAGE_SIZE, offset: partnersListPage * PAGE_SIZE, sort_by: partnersListSortBy || undefined, sort_dir: partnersListSortDir });
      set((state) => ({ partnersList: next, isLoadingPartnersList: false, partnersListPage: state.partnersListPage + 1, hasMorePartnersList: next.length === PAGE_SIZE, hasPrevPartnersList: true }));
    } catch (error: any) {
      console.error("Error fetching next partners page:", error);
      set({ isLoadingPartnersList: false });
    }
  },

  fetchPrevPagePartners: async (orgId: string) => {
    const PAGE_SIZE = 50;
    const { isLoadingPartnersList, hasPrevPartnersList, partnersListPage, partnersListCompanyType, partnersListSortBy, partnersListSortDir } = useProjectStore.getState();
    if (isLoadingPartnersList || !hasPrevPartnersList) return;
    const prevPage = partnersListPage - 2;
    set({ isLoadingPartnersList: true });
    try {
      const prev = await partnersApi.list({ org_id: orgId, company_type: partnersListCompanyType || undefined, limit: PAGE_SIZE, offset: prevPage * PAGE_SIZE, sort_by: partnersListSortBy || undefined, sort_dir: partnersListSortDir });
      set((state) => ({ partnersList: prev, isLoadingPartnersList: false, partnersListPage: state.partnersListPage - 1, hasMorePartnersList: true, hasPrevPartnersList: prevPage > 0 }));
    } catch (error: any) {
      console.error("Error fetching prev partners page:", error);
      set({ isLoadingPartnersList: false });
    }
  },

  setPartnersSort: async (orgId: string, sortBy: string, sortDir: string) => {
    const { partnersListCompanyType } = useProjectStore.getState();
    set({ partnersListSortBy: sortBy, partnersListSortDir: sortDir });
    const PAGE_SIZE = 50;
    set({ isLoadingPartnersList: true, partnersListPage: 0, hasPrevPartnersList: false });
    try {
      const partners = await partnersApi.list({ org_id: orgId, company_type: partnersListCompanyType || undefined, limit: PAGE_SIZE, offset: 0, sort_by: sortBy || undefined, sort_dir: sortDir });
      set({ partnersList: partners, isLoadingPartnersList: false, partnersListPage: 1, hasMorePartnersList: partners.length === PAGE_SIZE, hasPrevPartnersList: false });
    } catch (error: any) {
      set({ isLoadingPartnersList: false });
    }
  },

  // --- Broadcasts (API-connected) ---
  broadcasts: [],
  isLoadingBroadcasts: false,

  fetchBroadcasts: async (orgId: string) => {
    set({ isLoadingBroadcasts: true });
    try {
      const broadcasts = await broadcastsApi.list({ org_id: orgId });
      set({ broadcasts, isLoadingBroadcasts: false });
    } catch (error: any) {
      console.error("Error fetching broadcasts:", error);
      set({ broadcasts: [], isLoadingBroadcasts: false });
    }
  },

  setBroadcasts: (broadcasts: Broadcast[]) => {
    set({ broadcasts });
  },

  addBroadcast: (broadcast: Broadcast, orgId: string) => {
    set((state) => ({ broadcasts: [...state.broadcasts, broadcast] }));
    broadcastsApi.create({ ...broadcast, org_id: orgId } as any).catch((error) => {
      console.error("Error creating broadcast in API:", error);
    });
  },

  updateBroadcast: (broadcast: Broadcast) => {
    set((state) => ({
      broadcasts: state.broadcasts.map((b) =>
        b.id === broadcast.id ? broadcast : b,
      ),
    }));
    broadcastsApi.update(broadcast.id, broadcast).catch((error) => {
      console.error("Error updating broadcast in API:", error);
    });
  },

  removeBroadcasts: (ids: string[]) => {
    set((state) => ({
      broadcasts: state.broadcasts.filter((b) => !ids.includes(b.id)),
    }));
    ids.forEach((id) => {
      broadcastsApi.delete(id).catch((error) => {
        console.error("Error deleting broadcast from API:", error);
      });
    });
  },

  // --- Library documents (API-connected) ---
  libraryDocuments: [],
  isLoadingLibraryDocuments: false,

  fetchLibraryDocuments: async (orgId: string) => {
    set({ isLoadingLibraryDocuments: true });
    try {
      const libraryDocuments = await libraryDocumentsApi.list({ org_id: orgId });
      set({ libraryDocuments, isLoadingLibraryDocuments: false });
    } catch (error: any) {
      console.error("Error fetching library documents:", error);
      set({ libraryDocuments: [], isLoadingLibraryDocuments: false });
    }
  },

  addLibraryDocument: (doc: LibraryDocument, orgId: string) => {
    set((state) => ({
      libraryDocuments: [...state.libraryDocuments, doc],
    }));
    libraryDocumentsApi.create({ ...doc, org_id: orgId }).catch((error) => {
      console.error("Error creating library document in API:", error);
    });
  },

  removeLibraryDocuments: (ids: string[]) => {
    set((state) => ({
      libraryDocuments: state.libraryDocuments.filter(
        (d) => !ids.includes(d.id),
      ),
    }));
    ids.forEach((id) => {
      libraryDocumentsApi.delete(id).catch((error) => {
        console.error("Error deleting library document from API:", error);
      });
    });
  },

  // --- Email templates (API-connected) ---
  emailTemplates: [],
  isLoadingEmailTemplates: false,

  fetchEmailTemplates: async (orgId: string) => {
    set({ isLoadingEmailTemplates: true });
    try {
      const emailTemplates = await emailTemplatesApi.list({ org_id: orgId });
      set({ emailTemplates, isLoadingEmailTemplates: false });
    } catch (error: any) {
      console.error("Error fetching email templates:", error);
      set({ emailTemplates: [], isLoadingEmailTemplates: false });
    }
  },

  setEmailTemplates: (templates: EmailTemplate[]) => {
    set({ emailTemplates: templates });
  },

  addEmailTemplate: (template: EmailTemplate, orgId: string) => {
    set((state) => ({
      emailTemplates: [...state.emailTemplates, template],
    }));
    emailTemplatesApi.create({ ...template, org_id: orgId } as any).catch((error) => {
      console.error("Error creating email template in API:", error);
    });
  },

  updateEmailTemplate: (template: EmailTemplate) => {
    set((state) => ({
      emailTemplates: state.emailTemplates.map((t) =>
        t.id === template.id ? template : t,
      ),
    }));
    emailTemplatesApi.update(template.id, template).catch((error) => {
      console.error("Error updating email template in API:", error);
    });
  },

  removeEmailTemplates: (ids: string[]) => {
    set((state) => ({
      emailTemplates: state.emailTemplates.filter(
        (t) => !ids.includes(t.id),
      ),
    }));
    ids.forEach((id) => {
      emailTemplatesApi.delete(id).catch((error) => {
        console.error("Error deleting email template from API:", error);
      });
    });
  },

  // --- Project templates (API-connected) ---
  projectTemplates: [],
  isLoadingProjectTemplates: false,

  fetchProjectTemplates: async (orgId: string) => {
    set({ isLoadingProjectTemplates: true });
    try {
      const projectTemplates = await projectTemplatesApi.list({ org_id: orgId });
      set({ projectTemplates, isLoadingProjectTemplates: false });
    } catch (error: any) {
      console.error("Error fetching project templates:", error);
      set({ projectTemplates: [], isLoadingProjectTemplates: false });
    }
  },

  setProjectTemplates: (templates: Template[]) => {
    set({ projectTemplates: templates });
  },

  addProjectTemplate: (template: Template) => {
    set((state) => ({
      projectTemplates: [...state.projectTemplates, template],
    }));
  },

  updateProjectTemplate: (template: Template) => {
    set((state) => ({
      projectTemplates: state.projectTemplates.map((t) =>
        t.id === template.id ? template : t,
      ),
    }));
    projectTemplatesApi.update(template.id, {
      infos: template.infos,
      themes: template.themes,
      folders: template.folders,
      status: template.status,
    }).catch((error) => {
      console.error("Error updating project template in API:", error);
    });
  },

  removeProjectTemplates: (ids: string[]) => {
    set((state) => ({
      projectTemplates: state.projectTemplates.filter(
        (t) => !ids.includes(t.id),
      ),
    }));
    ids.forEach((id) => {
      projectTemplatesApi.delete(id).catch((error) => {
        console.error("Error deleting project template from API:", error);
      });
    });
  },

  // --- Form templates (API-connected) ---
  formTemplates: [],
  isLoadingFormTemplates: false,

  fetchFormTemplates: async (orgId: string) => {
    set({ isLoadingFormTemplates: true });
    try {
      const formTemplates = await formTemplatesApi.list({ org_id: orgId });
      set({ formTemplates, isLoadingFormTemplates: false });
    } catch (error: any) {
      console.error("Error fetching form templates:", error);
      set({ formTemplates: [], isLoadingFormTemplates: false });
    }
  },

  setFormTemplates: (templates: FormTemplate[]) => {
    set({ formTemplates: templates });
  },

  addFormTemplate: (template: FormTemplate) => {
    set((state) => ({
      formTemplates: [...state.formTemplates, template],
    }));
  },

  updateFormTemplate: (template: FormTemplate) => {
    set((state) => ({
      formTemplates: state.formTemplates.map((t) =>
        t.id === template.id ? template : t,
      ),
    }));
    formTemplatesApi.update(template.id, {
      name: template.name,
      categories: template.categories,
    }).catch((error) => {
      console.error("Error updating form template in API:", error);
    });
  },

  removeFormTemplates: (ids: string[]) => {
    set((state) => ({
      formTemplates: state.formTemplates.filter(
        (t) => !ids.includes(t.id),
      ),
    }));
    ids.forEach((id) => {
      formTemplatesApi.delete(id).catch((error) => {
        console.error("Error deleting form template from API:", error);
      });
    });
  },

  // --- Sync operations (API-connected) ---
  syncOperations: [],
  isLoadingSyncOperations: false,

  fetchSyncOperations: async (orgId: string) => {
    set({ isLoadingSyncOperations: true });
    try {
      const syncOperations = await syncOperationsApi.list({ org_id: orgId });
      set({ syncOperations, isLoadingSyncOperations: false });
    } catch (error: any) {
      console.error("Error fetching sync operations:", error);
      set({ syncOperations: [], isLoadingSyncOperations: false });
    }
  },

  setSyncOperations: (operations: SyncOperation[]) => {
    set({ syncOperations: operations });
  },

  clearCompletedSyncOperations: async (orgId: string) => {
    try {
      await syncOperationsApi.clearCompleted(orgId);
      set((state) => ({
        syncOperations: state.syncOperations.filter(
          (op) => op.status !== "completed",
        ),
      }));
    } catch (error: any) {
      console.error("Error clearing completed sync operations:", error);
    }
  },

  // --- Plannings (API-connected) ---
  plannings: [],
  isLoadingPlannings: false,

  fetchPlannings: async (orgId: string, projectId?: string) => {
    set({ isLoadingPlannings: true });
    try {
      const plannings = await planningsApi.list({ org_id: orgId, project_id: projectId });
      if (projectId) {
        set((state) => {
          const merged = [
            ...state.plannings.filter((p) => p.project_id !== projectId),
            ...plannings,
          ];
          return { plannings: merged, isLoadingPlannings: false };
        });
      } else {
        set({ plannings, isLoadingPlannings: false });
      }
    } catch (error: any) {
      console.error("Error fetching plannings:", error);
      set({ isLoadingPlannings: false });
    }
  },

  addPlanning: (planning: Planning) => {
    set((state) => ({ plannings: [...state.plannings, planning] }));
  },

  updatePlanning: (planning: Planning) => {
    set((state) => ({
      plannings: state.plannings.map((p) => (p.id === planning.id ? planning : p)),
    }));
    planningsApi.update(planning.id, {
      model: planning.model,
      start_date: planning.start_date,
      sections: planning.sections,
    }).catch((error) => {
      console.error("Error updating planning in API:", error);
    });
  },

  removePlannings: (ids: string[]) => {
    set((state) => ({
      plannings: state.plannings.filter((p) => !ids.includes(p.id)),
    }));
    ids.forEach((id) => {
      planningsApi.delete(id).catch((error) => {
        console.error("Error deleting planning from API:", error);
      });
    });
  },

  // --- Planning templates (API-connected) ---
  planningTemplates: [],
  isLoadingPlanningTemplates: false,

  fetchPlanningTemplates: async (orgId: string) => {
    set({ isLoadingPlanningTemplates: true });
    try {
      const planningTemplates = await planningTemplatesApi.list({ org_id: orgId });
      set({ planningTemplates, isLoadingPlanningTemplates: false });
    } catch (error: any) {
      console.error("Error fetching planning templates:", error);
      set({ planningTemplates: [], isLoadingPlanningTemplates: false });
    }
  },

  setPlanningTemplates: (templates: PlanningTemplate[]) => {
    set({ planningTemplates: templates });
  },

  addPlanningTemplate: (template: PlanningTemplate) => {
    set((state) => ({
      planningTemplates: [...state.planningTemplates, template],
    }));
  },

  updatePlanningTemplate: (template: PlanningTemplate) => {
    set((state) => ({
      planningTemplates: state.planningTemplates.map((t) =>
        t.id === template.id ? template : t,
      ),
    }));
    planningTemplatesApi.update(template.id, {
      name: template.name,
      model: template.model,
      sections: template.sections,
    }).catch((error) => {
      console.error("Error updating planning template in API:", error);
    });
  },

  removePlanningTemplates: (ids: string[]) => {
    set((state) => ({
      planningTemplates: state.planningTemplates.filter(
        (t) => !ids.includes(t.id),
      ),
    }));
    ids.forEach((id) => {
      planningTemplatesApi.delete(id).catch((error) => {
        console.error("Error deleting planning template from API:", error);
      });
    });
  },
}));
